import React, { useState } from 'react';
import {
  CollapsibleContainer,
  FolderItem,
  FolderHeader,
  FolderContent,
  FolderIcon,
  FolderName,
  CardCount,
  CardNumber,
  ExpandIcon,
  CardDisplayContainer,
  LeftCardContent,
  CardDetails,
  CardHeader,
  AttributesContainer,
  ButtonContainer,
  RemoveButton,
  ViewButton
} from './CollapsibleChecklist.styles';

const CollapsibleChecklist = ({
  data,
  onCardClick,
  basePath = '',
  onRemoveCard,
  isRemoving,
  canRemove = true,
  removeLabel = 'REMOVE FROM SET'
}) => {
  const [expandedFolders, setExpandedFolders] = useState({});
  const [initialized, setInitialized] = useState(false);

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Function to expand all folders recursively
  const expandAllFolders = (obj, path = '') => {
    const expanded = {};
    
    const traverse = (currentObj, currentPath) => {
      if (!currentObj || typeof currentObj !== 'object') return;
      
      for (const key in currentObj) {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        
        // If this is not a card (doesn't have cardNumber), it's a folder
        if (!currentObj[key].cardNumber) {
          expanded[newPath] = true;
          traverse(currentObj[key], newPath);
        }
      }
    };
    
    traverse(obj, path);
    return expanded;
  };

  // Initialize with all folders expanded
  React.useEffect(() => {
    if (data && !initialized) {
      const allExpanded = expandAllFolders(data);
      setExpandedFolders(allExpanded);
      setInitialized(true);
    }
  }, [data, initialized]);

  const getCardCount = (obj) => {
    if (!obj || typeof obj !== 'object') return 0;
    
    let count = 0;
    for (const key in obj) {
      if (obj[key] && typeof obj[key] === 'object' && obj[key].cardNumber) {
        count++;
      } else if (obj[key] && typeof obj[key] === 'object') {
        count += getCardCount(obj[key]);
      }
    }
    return count;
  };

  const isCard = (obj) => {
    return obj && typeof obj === 'object' && obj.cardNumber;
  };

  const renderFolder = (key, value, level = 0, path = '') => {
    const currentPath = path ? `${path}.${key}` : key;
    const isExpanded = expandedFolders[currentPath];
    const cardCount = getCardCount(value);
    const isCardItem = isCard(value);

    if (isCardItem) {
      const isCollected = value.isCollected !== undefined ? value.isCollected : (value.variations && value.variations.length > 0);
      
      return (
        <CardDisplayContainer key={key} $isCollected={isCollected}>
          <LeftCardContent>
            <CardNumber>
              <h4>{value.cardNumber}</h4>
            </CardNumber>
            <CardDetails>
              <CardHeader>
                {Array.isArray(value.name) ? value.name.map((name, index) => (
                  <span key={index}>
                    <h3 style={{ display: 'inline' }}>{name}</h3>
                    {index < value.name.length - 1 && ' • '}
                  </span>
                )) : (
                  <span>
                    <h3 style={{ display: 'inline' }}>{value.name}</h3>
                  </span>
                )}
                {!isCollected && (
                  <span style={{
                    background: 'rgba(255, 193, 7, 0.2)',
                    border: '1px solid rgba(255, 193, 7, 0.5)',
                    borderRadius: '4px',
                    color: '#FFC107',
                    fontSize: '10px',
                    padding: '2px 6px',
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    fontWeight: '600'
                  }}>
                    WISHLIST
                  </span>
                )}
                {value.badge && (
                  <span style={{
                    background: 'rgba(255, 193, 7, 0.2)',
                    border: '1px solid rgba(255, 193, 7, 0.5)',
                    borderRadius: '4px',
                    color: '#FFC107',
                    fontSize: '10px',
                    padding: '2px 6px',
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    fontWeight: '600'
                  }}>
                    {value.badge}
                  </span>
                )}
              </CardHeader>
              <AttributesContainer>
                {value.cardAttributes && value.cardAttributes.length !== 0 && (
                  <div className='card-attributes-container'>
                    {value.cardAttributes.map(attribute => (
                      <span key={attribute} style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '10px',
                        padding: '2px 6px',
                        whiteSpace: 'nowrap'
                      }}>
                        {attribute}
                      </span>
                    ))}
                  </div>
                )}
                {value.cardAttributes && value.cardAttributes.length !== 0 && value.variations && value.variations.length !== 0 && (
                  <h4>•</h4>
                )}
                {value.variations && value.variations.length > 0 && (
                  <div className='card-attributes-container'>
                    {value.variations.map(parallel => (
                      <span
                        key={parallel.cardParallelName}
                        style={{
                          background: parallel.cardColor ? `${parallel.cardColor}20` : 'rgba(255, 255, 255, 0.1)',
                          border: `1px solid ${parallel.cardColor || 'rgba(255, 255, 255, 0.2)'}`,
                          borderRadius: '4px',
                          color: parallel.cardColor || 'rgba(255, 255, 255, 0.8)',
                          fontSize: '10px',
                          padding: '2px 6px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {`${parallel.cardParallelName} ${((parallel.cardPrintNumber !== null) && (parallel.cardSupply !== null)) ? `(${parallel.cardPrintNumber}/${parallel.cardSupply})` : ''} (QTY: ${parallel.cardQty})`}
                      </span>
                    ))}
                  </div>
                )}
                {value.quantity > 1 && (
                  <div className='card-attributes-container'>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '10px',
                      padding: '2px 6px',
                      whiteSpace: 'nowrap'
                    }}>
                      Total QTY: {value.quantity}
                    </span>
                  </div>
                )}
              </AttributesContainer>
            </CardDetails>
          </LeftCardContent>
          <ButtonContainer>
            {canRemove && (
              <RemoveButton
                onClick={() => onRemoveCard && onRemoveCard(value.id)}
                disabled={isRemoving}
              >
                <h4>{removeLabel}</h4>
              </RemoveButton>
            )}
            <ViewButton $isActive={!!value.checklistPath} onClick={() => onCardClick && onCardClick(value, currentPath)}>
              <h4>VIEW IN CHECKLIST</h4>
            </ViewButton>
          </ButtonContainer>
        </CardDisplayContainer>
      );
    }

    const hasCards = cardCount > 0;

    return (
      <FolderItem key={key} $level={level}>
        <FolderHeader 
          onClick={() => hasCards && toggleFolder(currentPath)}
          $isClickable={hasCards}
          $isExpanded={isExpanded}
        >
          <FolderIcon>
            {hasCards ? (
              isExpanded ? '📂' : '📁'
            ) : (
              '📄'
            )}
          </FolderIcon>
          
          {hasCards && (
            <ExpandIcon $isExpanded={isExpanded}>
              {isExpanded ? '▼' : '▶'}
            </ExpandIcon>
          )}
          
          <FolderName>{key}</FolderName>
          
          {hasCards && (
            <CardCount>{cardCount} cards</CardCount>
          )}
        </FolderHeader>

        {hasCards && isExpanded && (
          <FolderContent>
            {Object.entries(value).map(([subKey, subValue]) => 
              renderFolder(subKey, subValue, level + 1, currentPath)
            )}
          </FolderContent>
        )}
      </FolderItem>
    );
  };

  return (
    <CollapsibleContainer>
      {Object.entries(data).map(([key, value]) => 
        renderFolder(key, value)
      )}
    </CollapsibleContainer>
  );
};

export default CollapsibleChecklist;
